/**
 * Utility functions for handling Firestore timestamps
 */

/**
 * Converts a Firestore timestamp to a readable string
 * @param {Object|Date|string} timestamp - The timestamp to convert
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time in the output
 * @param {boolean} options.short - Whether to use short format
 * @returns {string} Formatted timestamp string
 */
export const formatTimestamp = (timestamp, options = {}) => {
  const { includeTime = true, short = false } = options;
  
  if (!timestamp) {
    return 'Recently';
  }

  let date;
  
  // Handle Firestore timestamp objects
  if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
    date = timestamp.toDate();
  }
  // Handle regular Date objects
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  // Handle string timestamps
  else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  }
  // Handle timestamp objects with seconds/nanoseconds
  else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  }
  // Handle numeric timestamps
  else if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  }
  else {
    return 'Recently';
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Recently';
  }

  try {
    if (short) {
      return includeTime ? 
        date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
        date.toLocaleDateString();
    } else {
      return includeTime ? 
        date.toLocaleString() :
        date.toLocaleDateString();
    }
  } catch (error) {
    return 'Recently';
  }
};

/**
 * Converts a Firestore timestamp to a relative time string (e.g., "2 hours ago")
 * @param {Object|Date|string} timestamp - The timestamp to convert
 * @returns {string} Relative time string
 */
export const getRelativeTime = (timestamp) => {
  if (!timestamp) {
    return 'Recently';
  }

  let date;
  
  // Handle Firestore timestamp objects
  if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
    date = timestamp.toDate();
  }
  // Handle regular Date objects
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  // Handle string timestamps
  else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  }
  // Handle timestamp objects with seconds/nanoseconds
  else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  }
  // Handle numeric timestamps
  else if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  }
  else {
    return 'Recently';
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Recently';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
};

/**
 * Checks if a value is a valid timestamp
 * @param {any} value - The value to check
 * @returns {boolean} True if it's a valid timestamp
 */
export const isValidTimestamp = (value) => {
  if (!value) return false;
  
  // Firestore timestamp object
  if (value && typeof value === 'object' && value.toDate) {
    return true;
  }
  
  // Regular Date object
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }
  
  // String timestamp
  if (typeof value === 'string') {
    const date = new Date(value);
    return !isNaN(date.getTime());
  }
  
  // Timestamp object with seconds/nanoseconds
  if (value && typeof value === 'object' && value.seconds) {
    return true;
  }
  
  // Numeric timestamp
  if (typeof value === 'number') {
    const date = new Date(value);
    return !isNaN(date.getTime());
  }
  
  return false;
};
